import { Dialog as DialogPrimitive } from 'radix-ui';
import { Button, Dialog, DialogDescription, DialogHeader, DialogOverlay, DialogPortal, DialogScreens, DialogTitle, Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@music-vine/cadence/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import useResetPasswordLinkHandler from '@/components/Modals/AuthDialog/useResetPasswordLinkHandler';
import { cn } from '@/lib/utils';
import { closeStageAtom, openSceneAtom, stageAtom } from './atom';
import { scenes } from './scenes';
import { usePlanStepCheckoutReturn } from './signupPlanStep';
import { usePostDownloadDataCapture } from './usePostDownloadDataCapture';
import { usePostPaymentDownload } from './usePostPaymentDownload';
import type { SceneDefinition, SceneKey, SceneProps, Surface } from './types';
export { useStage } from './useStage';
export type { SceneKey } from './types';

// Single switch for every animation on the mobile full-screen surface — both
// the open/close fade on the surface itself AND the inter-screen slide/resize
// inside `DialogScreens`. Per DEV-6010 the design reads better without them
// when the dialog covers the whole viewport; flip this to `true` to put them
// back without touching anything else.
const ENABLE_MOBILE_FULLSCREEN_ANIMATIONS = false;
const FULLSCREEN_SURFACE_ANIMATION_CLASSES = ENABLE_MOBILE_FULLSCREEN_ANIMATIONS ? cn('data-[state=closed]:animate-out data-[state=open]:animate-in', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0') : '';
const baseSurfaceClasses = cn('fixed z-50 flex flex-col bg-white shadow-lg outline-hidden duration-200');
const surfaceClasses: Record<Exclude<Surface, 'drawer'>, string> = {
  modal: cn('top-1/2 left-1/2 max-h-[90dvh] w-full max-w-[616px] overflow-hidden', 'translate-x-[-50%] translate-y-[-50%]', 'rounded-lg border border-gray-150 border-solid dark:border-gray-800 dark:bg-gray-875', 'data-[state=closed]:animate-out data-[state=open]:animate-in', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'),
  fullscreen: cn('inset-0 h-full w-full dark:bg-gray-950', FULLSCREEN_SURFACE_ANIMATION_CLASSES)
};

// Scenes differ in width as well as height (e.g. 482px sign-up → 864px plan
// step). Timed to DialogScreens' defaults: wait out the 100ms exit slide, then
// resize over its 140ms height animation with the same ease, so both axes grow
// together before the new screen slides in. Inline so it can't lose a
// stylesheet-order fight with the surface's `duration-200`.
const MODAL_WIDTH_TRANSITION = {
  transition: 'max-width 140ms cubic-bezier(0.32, 0.72, 0, 1) 100ms'
};
const closeButtonClasses = cn('absolute top-3 right-4 z-10 opacity-100 ring-offset-white transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-500 md:top-4 dark:ring-offset-gray-950 dark:data-[state=open]:bg-gray-800 dark:data-[state=open]:text-gray-400 dark:focus:ring-gray-300');

// The mobile fullscreen top nav backs the X with a filled circle so it reads
// over scrolling content (DEV-6704); the other surfaces keep the borderless
// icon.
const closeButton = (variant: 'transparent' | 'light' = 'transparent') => <Button aria-label="Close" borderRadius="full" icon size="default" variant={variant} data-sentry-element="Button" data-sentry-component="closeButton" data-sentry-source-file="index.tsx">
    <X className="size-4" data-sentry-element="X" data-sentry-source-file="index.tsx" />
    <span className="sr-only">Close</span>
  </Button>;
type StageSurfaceProps = {
  onOpenChange: (next: boolean) => void;
  open: boolean;
  scene: SceneDefinition | null;
  /** Rendered scene key, exposed as `data-dialog-screen` for visual tests */
  sceneKey: SceneKey | null;
  screens: ReactNode;
};

// Only ever picked on mobile, so `hideMobileClose` applies unconditionally.
const StageDrawer = ({
  onOpenChange,
  open,
  scene,
  sceneKey,
  screens
}: StageSurfaceProps) => <Drawer onOpenChange={onOpenChange} open={open} data-sentry-element="Drawer" data-sentry-component="StageDrawer" data-sentry-source-file="index.tsx">
    <DrawerContent className={cn('overflow-hidden p-0', scene?.surfaceClassName?.drawer)} data-dialog-screen={sceneKey ?? undefined} data-sentry-element="DrawerContent" data-sentry-source-file="index.tsx">
      {scene && screens ? <>
          <DrawerHeader className="sr-only">
            <DrawerTitle>{scene.a11y.title}</DrawerTitle>
            {scene.a11y.description ? <DrawerDescription>{scene.a11y.description}</DrawerDescription> : null}
          </DrawerHeader>
          {screens}
          {scene.hideMobileClose ? null : <DrawerClose asChild className={closeButtonClasses}>
              {closeButton()}
            </DrawerClose>}
        </> : null}
    </DrawerContent>
  </Drawer>;
const StageDialog = ({
  isMobile,
  onOpenChange,
  open,
  overlayClassName,
  scene,
  sceneKey,
  screens,
  surface
}: StageSurfaceProps & {
  isMobile: boolean | undefined;
  overlayClassName?: string;
  surface: Exclude<Surface, 'drawer'>;
}) => <Dialog onOpenChange={onOpenChange} open={open} data-sentry-element="Dialog" data-sentry-component="StageDialog" data-sentry-source-file="index.tsx">
    <DialogPortal data-sentry-element="DialogPortal" data-sentry-source-file="index.tsx">
      <DialogOverlay className={overlayClassName} data-sentry-element="DialogOverlay" data-sentry-source-file="index.tsx" />
      <DialogPrimitive.Content className={cn(baseSurfaceClasses, surfaceClasses[surface], scene?.surfaceClassName?.[surface])} data-dialog-screen={sceneKey ?? undefined} style={surface === 'modal' ? MODAL_WIDTH_TRANSITION : undefined} onOpenAutoFocus={event => {
      event.preventDefault();
      const content = event.currentTarget;
      if (content instanceof HTMLElement) {
        content.focus({
          preventScroll: true
        });
      }
    }} data-sentry-element="DialogPrimitive.Content" data-sentry-source-file="index.tsx">
        {scene && screens ? <>
            <DialogHeader className="sr-only">
              <DialogTitle>{scene.a11y.title}</DialogTitle>
              {scene.a11y.description ? <DialogDescription>{scene.a11y.description}</DialogDescription> : null}
            </DialogHeader>
            {screens}
            {isMobile && scene.hideMobileClose ? null : <DialogPrimitive.Close asChild className={closeButtonClasses}>
                {closeButton(surface === 'fullscreen' ? 'light' : 'transparent')}
              </DialogPrimitive.Close>}
          </> : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  </Dialog>;
const pickSurface = (scene: SceneDefinition, isMobile: boolean | undefined): Surface => {
  if (isMobile && scene.surface.mobile) {
    return scene.surface.mobile;
  }
  return scene.surface.default;
};

/**
 * Globally-mounted stage that hosts every multi-screen flow registered in
 * {@link ./scenes}. Owns one portal, one overlay, and one content surface;
 * scene swaps animate inside via {@link DialogScreens} so the overlay never
 * unmounts and cross-flow handoffs never flicker.
 *
 * Mount once in `_app` (or alongside other globally-mounted modals). Call
 * sites use {@link useStage} to open, close, and navigate.
 *
 * @example
 * // src/pages/_app.page.tsx
 * <DialogStage />
 *
 * @example
 * // Anywhere
 * const { open, goTo, close } = useStage();
 * open('auth.login');
 * goTo('onboarding.questionnaire');  // cross-flow handoff, one overlay
 * close();
 */
const DialogStage = () => {
  const {
    active,
    direction
  } = useAtomValue(stageAtom);
  const openScene = useSetAtom(openSceneAtom);
  const closeStage = useSetAtom(closeStageAtom);
  const isMobile = useIsMobile();
  // Watches the URL for ?reset-password-hash= / ?reset-password-error= and
  // opens the relevant scene. Lives here because the stage is the surface
  // those scenes target — no extra plumbing required at call sites.
  useResetPasswordLinkHandler();
  // Reopens the payment-confirmation fallback on the /subscribe success screen
  // when the user came from the download journey (DEV-6516).
  usePostPaymentDownload();
  // Presents the data-capture questionnaire a beat after a download-journey
  // signup's first download completes (DEV-6521).
  usePostDownloadDataCapture();
  // Same idea for an abandoned plan-step checkout (DEV-7090).
  usePlanStepCheckoutReturn();
  // Takes a download-journey checkout back to its page and reopens the
  // download there (DEV-6516).
  usePostPaymentDownload();
  // Presents the data-capture questionnaire a beat after a download-journey
  // signup's first download completes (DEV-6521).
  usePostDownloadDataCapture();

  // `useSetAtom` already returns referentially stable setters, so we can
  // alias them directly. `goTo`'s typed wider arg gets narrowed via
  // SceneProps['goTo']'s contravariant function-arg assignability.
  const close: SceneProps['close'] = closeStage;
  const goTo: SceneProps['goTo'] = openScene;
  const sceneProps: SceneProps = useMemo(() => ({
    close,
    goTo
  }), [close, goTo]);
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      close();
    }
  };

  // Keep the last open scene around so the close animation has a stable
  // surface + content to render against. Without this, the moment `active`
  // flips to null the surface jumps to the default and the exit animation
  // plays from the wrong position.
  const lastSceneRef = useRef<{
    key: SceneKey;
    scene: SceneDefinition;
  } | null>(null);
  if (active) {
    lastSceneRef.current = {
      key: active as SceneKey,
      scene: scenes[active as SceneKey]
    };
  }
  const rendered = lastSceneRef.current;
  const scene = rendered?.scene ?? null;
  const surface = scene ? pickSurface(scene, isMobile) : 'modal';
  const Content = scene?.content;

  // The full-screen surface covers the whole viewport, so the dark overlay
  // is redundant — and worse, its own fade-out outlasts the surface's exit
  // (which is animation-less per ENABLE_MOBILE_FULLSCREEN_ANIMATIONS), so the
  // dimmer is briefly visible against the page beneath. Drop both the dim
  // tint and the open/close fade for fullscreen.
  const overlayClassName = surface === 'fullscreen' ? 'bg-transparent data-[state=closed]:animate-none data-[state=open]:animate-none' : undefined;

  // Defer opening until `useIsMobile` has resolved. On the first client
  // render it returns `undefined`, which forces `pickSurface` to fall back
  // to the desktop `modal` surface — so a mobile user would briefly see a
  // centred modal flash in before the effect tick re-renders with the
  // `fullscreen` surface. Holding `open` false for that one frame skips the
  // mismatched paint entirely.
  const ready = isMobile !== undefined;
  const open = ready && active !== null;
  const screens = scene && Content ? <DialogScreens activeScreen={rendered?.key ?? ''} className={scene?.screensClassName} direction={direction} {...surface === 'fullscreen' && !ENABLE_MOBILE_FULLSCREEN_ANIMATIONS ? {
    resizeDuration: 0,
    slideDuration: 0
  } : {}}>
        <Content {...sceneProps} />
      </DialogScreens> : null;
  if (surface === 'drawer') {
    return <StageDrawer onOpenChange={handleOpenChange} open={open} scene={scene} sceneKey={rendered?.key ?? null} screens={screens} />;
  }
  return <StageDialog isMobile={isMobile} onOpenChange={handleOpenChange} open={open} overlayClassName={overlayClassName} scene={scene} sceneKey={rendered?.key ?? null} screens={screens} surface={surface} data-sentry-element="StageDialog" data-sentry-component="DialogStage" data-sentry-source-file="index.tsx" />;
};
export default DialogStage;